/**
 * Confine a project's stylesheet to part of the page.
 *
 * These stylesheets theme the interface through Bootstrap's own classes —
 * .card, .table, .nav-tabs, .modal-content — plus body, p and a. That is what
 * makes a project feel like itself, and it is also why trading, settings and
 * platform panels came out wearing the artwork's colours: nothing in the
 * stylesheet distinguishes the art from the machinery around it.
 *
 * So every selector is prefixed with a scope, and the panels that should stay
 * neutral render outside it. Prefixing rather than blocking keeps the whole
 * stylesheet working where it is wanted; a rule that tried to undo it after the
 * fact would have to guess at every property a project might set.
 *
 * body, html and :root become the scope itself: a project setting a page
 * background means the background behind the art, which is what the scope
 * element covers.
 */

// Left alone: these define things rather than target elements.
const OPAQUE_AT_RULES = /^@(keyframes|-\w+-keyframes|font-face|import|charset|namespace|counter-style|property|font-feature-values)/i;
// Recursed into: these wrap ordinary rules in a condition.
const NESTING_AT_RULES = /^@(media|supports|document|layer|container)/i;

const ROOT_SELECTORS = /^(html|body|:root)$/i;

/**
 * Dialogs became floating panels, but a project's stylesheet still describes
 * dialogs. Rules aimed at Bootstrap's modal parts are re-aimed at the panel
 * parts that replaced them, so a project that themed its dialogs keeps them
 * themed rather than getting a plain white window.
 */
export const PANEL_FROM_MODAL = {
  '.modal-content': '.dock-panel',
  '.modal-body': '.dock-panel__body',
  '.modal-header': '.dock-panel__bar',
  '.modal-title': '.dock-panel__title',
  '.modal-footer': '.dock-panel__footer',
};

/**
 * Split a selector list on commas that are not inside brackets or quotes.
 */
function splitSelectors(list) {
  const out = [];
  let depth = 0;
  let quote = null;
  let current = '';
  for (const ch of list) {
    if (quote) {
      current += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") { quote = ch; current += ch; continue; }
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth--;
    if (ch === ',' && depth === 0) { out.push(current); current = ''; continue; }
    current += ch;
  }
  if (current.trim()) out.push(current);
  return out;
}

function prefixSelector(selector, scope, dropRootRules, translate) {
  let trimmed = selector.trim();
  if (!trimmed) return null;

  if (translate) {
    // Only rules that actually mention a dialog part are carried across; the
    // rest already applied to the page and would be duplicated for nothing.
    let touched = false;
    for (const [from, to] of Object.entries(translate)) {
      if (trimmed.includes(from)) { trimmed = trimmed.split(from).join(to); touched = true; }
    }
    if (!touched) return null;
  }
  // Already scoped, or a nested/parent reference we should not touch.
  if (trimmed.startsWith(scope)) return trimmed;

  if (ROOT_SELECTORS.test(trimmed)) {
    // A bare body/html/:root rule is the page itself. Mapping it onto a scope
    // that is not the page paints the wrong thing — a project's page
    // background became every dialog's background — so for those scopes the
    // rule is dropped rather than redirected.
    return dropRootRules ? null : scope;
  }

  // "body .cell" and the like are descendant rules, not page rules: the root
  // part is replaced by the scope and the rest kept, in either mode.
  const parts = trimmed.split(/\s+/);
  if (ROOT_SELECTORS.test(parts[0])) return [scope, ...parts.slice(1)].join(' ');
  return `${scope} ${trimmed}`;
}

/**
 * Rewrite a stylesheet so every rule applies only inside `scope`.
 */
export function scopeCss(css, scope = '#root', options = {}) {
  if (!css || typeof css !== 'string') return css;
  const dropRootRules = !!options.dropRootRules;
  const translate = options.translate || null;

  let out = '';
  let index = 0;

  while (index < css.length) {
    const open = css.indexOf('{', index);
    if (open === -1) { out += css.slice(index); break; }

    const prelude = css.slice(index, open);
    const trimmed = prelude.trim();

    // Find the matching close brace for this block.
    let depth = 0;
    let close = open;
    for (; close < css.length; close++) {
      if (css[close] === '{') depth++;
      else if (css[close] === '}') { depth--; if (depth === 0) break; }
    }
    const body = css.slice(open + 1, close);

    if (OPAQUE_AT_RULES.test(trimmed)) {
      out += `${prelude}{${body}}`;
    } else if (NESTING_AT_RULES.test(trimmed)) {
      out += `${prelude}{${scopeCss(body, scope, options)}}`;
    } else {
      const selectors = splitSelectors(prelude)
        .map((s) => prefixSelector(s, scope, dropRootRules, translate))
        .filter(Boolean);
      // Every selector dropped means the rule does not belong in this scope at
      // all; emitting the block unscoped would put it back on the whole page.
      if (selectors.length) out += `${selectors.join(', ')}{${body}}`;
    }

    index = close + 1;
  }

  return out;
}

export default scopeCss;
